import type React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { VolumeOffIcon, VolumeUpIcon } from "@heroicons/react/outline";

interface VideoControlsProps {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  /** Current time relative to segment start (0 = start of cropped segment) */
  currentTime: number;
  /** Total duration of the cropped segment in seconds */
  duration: number;
  /** Segment start time in video (for seeking) */
  segmentStart: number;
  /** Callback when time changes via seeking */
  onSeek?: (time: number) => void;
}

const VOLUME_STORAGE_KEY = "wixar-video-volume";
const MUTED_STORAGE_KEY = "wixar-video-muted";

/**
 * Format time in seconds to MM:SS format
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Video playback controls component
 * Shows play/pause button, seek bar, and time display
 */
const VideoControls: React.FC<VideoControlsProps> = ({
  videoRef,
  currentTime,
  duration,
  segmentStart,
  onSeek,
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState(0);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
    return saved !== null ? parseFloat(saved) : 0.5;
  });
  const [isMuted, setIsMuted] = useState(() => {
    const saved = localStorage.getItem(MUTED_STORAGE_KEY);
    return saved !== null ? saved === "true" : true;
  });
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const volumeControlRef = useRef<HTMLDivElement>(null);

  const attachedVideoRef = useRef<HTMLVideoElement | null>(null);

  const handlePlayEvent = useCallback(() => setIsPlaying(true), []);
  const handlePauseEvent = useCallback(() => setIsPlaying(false), []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return undefined;
    }

    if (video === attachedVideoRef.current) {
      setIsPlaying(!video.paused);
      return undefined;
    }

    if (attachedVideoRef.current) {
      attachedVideoRef.current.removeEventListener("play", handlePlayEvent);
      attachedVideoRef.current.removeEventListener("pause", handlePauseEvent);
    }

    attachedVideoRef.current = video;
    video.addEventListener("play", handlePlayEvent);
    video.addEventListener("pause", handlePauseEvent);

    video.volume = volume;
    video.muted = isMuted;

    setIsPlaying(!video.paused);

    return () => {
      if (attachedVideoRef.current) {
        attachedVideoRef.current.removeEventListener("play", handlePlayEvent);
        attachedVideoRef.current.removeEventListener("pause", handlePauseEvent);
        attachedVideoRef.current = null;
      }
    };
  }, [currentTime, handlePlayEvent, handlePauseEvent, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.volume = volume;
    video.muted = isMuted;
  }, [videoRef, volume, isMuted]);

  useEffect(() => {
    localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString());
  }, [volume]);

  useEffect(() => {
    localStorage.setItem(MUTED_STORAGE_KEY, isMuted.toString());
  }, [isMuted]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(e.target as Node)) {
        setShowVolumeSlider(false);
      }
    };

    if (showVolumeSlider) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [showVolumeSlider]);

  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch((e) => console.warn("Play failed:", e));
    } else {
      video.pause();
    }
  }, [videoRef]);

  const handleToggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0) {
      setIsMuted(false);
    }
  }, []);

  const getTimeFromPosition = useCallback(
    (clientX: number): number => {
      const progressBar = progressBarRef.current;
      if (!progressBar || duration <= 0) return 0;

      const rect = progressBar.getBoundingClientRect();
      const position = (clientX - rect.left) / rect.width;
      const clampedPosition = Math.max(0, Math.min(1, position));
      return clampedPosition * duration;
    },
    [duration],
  );

  const seekTo = useCallback(
    (relativeTime: number) => {
      const video = videoRef.current;
      if (!video) return;

      const absoluteTime = segmentStart + relativeTime;
      video.currentTime = absoluteTime;
      onSeek?.(relativeTime);
    },
    [videoRef, segmentStart, onSeek],
  );

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const time = getTimeFromPosition(e.clientX);
      seekTo(time);
    },
    [getTimeFromPosition, seekTo],
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const time = getTimeFromPosition(clientX);
      setDragValue(time);
    },
    [getTimeFromPosition],
  );

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const time = getTimeFromPosition(clientX);
      setDragValue(time);
    };

    const handleEnd = (e: MouseEvent | TouchEvent) => {
      const clientX = "changedTouches" in e ? e.changedTouches[0].clientX : e.clientX;
      const time = getTimeFromPosition(clientX);
      seekTo(time);
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleMove);
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, getTimeFromPosition, seekTo]);

  const displayTime = isDragging ? dragValue : currentTime;
  const progressPercent = duration > 0 ? (displayTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
      <button
        onClick={handlePlayPause}
        className="text-white hover:text-green-400 transition-colors focus:outline-none"
        title={isPlaying ? t("statsNew.gazeHeatmap.pause") : t("statsNew.gazeHeatmap.play")}
      >
        {isPlaying ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      <div
        ref={volumeControlRef}
        className="relative flex items-center gap-2 group/volume"
        onMouseEnter={() => setShowVolumeSlider(true)}
        onMouseLeave={() => setShowVolumeSlider(false)}
      >
        <button
          onClick={handleToggleMute}
          className="text-white hover:text-green-400 transition-colors focus:outline-none"
          title={isMuted ? t("statsNew.gazeHeatmap.unmute") : t("statsNew.gazeHeatmap.mute")}
        >
          {isMuted || volume === 0 ? (
            <VolumeOffIcon className="h-5 w-5" strokeWidth={2} />
          ) : volume < 0.5 ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.536 8.464a5 5 0 010 7.072M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            </svg>
          ) : (
            <VolumeUpIcon className="h-5 w-5" strokeWidth={2} />
          )}
        </button>

        <div
          className={`flex items-center overflow-hidden transition-all duration-200 ${
            showVolumeSlider ? "w-20 opacity-100" : "w-0 opacity-0"
          }`}
        >
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={handleVolumeChange}
            className="w-full h-1 bg-white/30 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:shadow-md"
          />
        </div>
      </div>

      <span className="text-white text-sm font-mono min-w-[40px]">{formatTime(displayTime)}</span>

      <div
        ref={progressBarRef}
        className="relative flex-1 h-2 min-w-[100px] cursor-pointer group"
        onClick={handleProgressClick}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="absolute inset-0 bg-white/30 rounded-full" />

        <div
          className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-none"
          style={{ width: `${progressPercent}%` }}
        />

        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progressPercent}% - 6px)` }}
        />
      </div>

      <span className="text-white text-sm font-mono min-w-[40px]">{formatTime(duration)}</span>
    </div>
  );
};

export default VideoControls;
